import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr, track } from "../api";
import { Page, Logo, WhatsAppButton } from "../components/Layout";
import { Check, Camera, X, ChevronRight } from "lucide-react";

const inp = "w-full border border-gray-200 rounded-xl px-4 py-3 mt-1 focus:border-violet-500 outline-none";
const TREATMENTS = [
  { id: "shaved", label: "Effetto rasato", desc: "Per ricreare visivamente l'effetto dei follicoli su capelli rasati." },
  { id: "density", label: "Densità", desc: "Per ridurre visivamente il contrasto tra cute e capelli nelle zone diradate." },
];

export default function TricoPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const formRef = useRef(null);
  const [treatment, setTreatment] = useState("");
  const [video, setVideo] = useState(null); // null | true | false
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [photos, setPhotos] = useState([]); // {url, preview}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const price = video === true ? 1000 : 1250;
  // Referral: memorizza il codice invito così l'attribuzione sopravvive alla chiusura del browser.
  useEffect(() => {
    const r = params.get("ref");
    if (r) localStorage.setItem("mas_ref_trico", r);
  }, [params]);
  const refCode = () => params.get("ref") || localStorage.getItem("mas_ref_trico") || "";

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    for (const file of files) {
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { toast.error("Questa immagine non è supportata."); continue; }
      if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)."); continue; }
      try {
        const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
        const { data } = await api.post("/tricopigmentazione/photo", { data: dataUrl });
        setPhotos((p) => [...p, { url: data.url, preview: dataUrl }]);
      } catch (er) { toast.error(apiErr(er)); }
    }
    e.target.value = "";
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const scrollForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    if (!treatment) return toast.error("Seleziona il trattamento di interesse.");
    if (!f.name.trim()) return toast.error("Inserisci il tuo nome.");
    if (!f.phone.trim()) return toast.error("Controlla il numero di telefono.");
    setBusy(true);
    try {
      const { data } = await api.post("/tricopigmentazione/lead", {
        name: f.name, phone: f.phone, email: f.email, treatment_type: treatment,
        video_review: video === true, photos: photos.map((p) => p.url), ref: refCode(),
      });
      track("trico_lead", { treatment, video: video === true });
      setDone({ treatment, video: video === true, price: data.quoted_price });
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  if (done) {
    const tl = TREATMENTS.find((t) => t.id === done.treatment);
    return (
      <Page>
        <div className="text-center pt-14">
          <Logo className="inline-block" sub />
          <Check size={52} className="mx-auto mt-6 text-green-600" />
          <h1 className="serif text-2xl mt-4" data-testid="trico-success">Richiesta inviata</h1>
          <p className="text-gray-600 mt-2">MAS ha ricevuto la tua richiesta di consulenza. Ti contatteremo per valutare insieme il trattamento più adatto.</p>
          <div className="card p-4 mt-5 text-sm text-left inline-block">
            <div>Trattamento: <b>{tl?.label}</b></div>
            <div>Video recensione: <b>{done.video ? "Sì" : "No"}</b></div>
            <div>Prezzo indicativo: <b>{done.price === 1000 ? "1.000 €" : "1.250 €"}</b></div>
          </div>
          <button data-testid="trico-home" onClick={() => nav("/")} className="btn-primary w-full mt-6">Torna alla Home</button>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      {/* Hero */}
      <div className="text-center">
        <Logo className="inline-block" sub />
        <h1 className="serif text-3xl mt-4">Tricopigmentazione</h1>
        <p className="text-sm text-gray-500 mt-2">Scopri il trattamento MAS. Effetto rasato o densità: un risultato naturale, studiato su di te.</p>
        <button data-testid="trico-cta-top" onClick={scrollForm} className="btn-dark w-full mt-5">Richiedi una consulenza</button>
      </div>

      {/* Due percorsi */}
      <h2 className="serif text-xl mt-9 mb-3">Scegli il tuo percorso</h2>
      <div className="space-y-3">
        {TREATMENTS.map((t) => (
          <button key={t.id} data-testid={`trico-choose-${t.id}`} onClick={() => { setTreatment(t.id); scrollForm(); }}
            className={`w-full text-left card p-4 border-2 transition-colors ${treatment === t.id ? "border-violet-600 bg-violet-50" : "border-transparent"}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{t.label}</div>
              <ChevronRight size={18} className="text-violet-600" />
            </div>
            <p className="text-sm text-gray-600 mt-1">{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Prezzo / video recensione */}
      <div className="bg-[#0f0f12] text-white rounded-2xl p-6 mt-8">
        <div className="text-[0.62rem] tracking-[0.3em] uppercase text-gray-400">Trattamento MAS</div>
        <div className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span>Prezzo trattamento:</span><span className="font-semibold">1.250 €</span></div>
          <div className="flex justify-between"><span>Con video recensione a fine trattamento:</span><span className="font-semibold text-violet-300">1.000 €</span></div>
        </div>
        <div className="border-t border-white/15 mt-3 pt-3 flex justify-between items-center">
          <span className="text-sm">Prezzo{video === true ? " finale" : " indicativo"}:</span>
          <span className="serif text-3xl text-violet-300" data-testid="trico-price">{price === 1000 ? "1.000 €" : "1.250 €"}</span>
        </div>
        <p className="text-[0.72rem] text-gray-400 mt-3 leading-relaxed">Se sarai disponibile a lasciare una video recensione a fine trattamento, ti applicheremo uno sconto di 250 €. La video recensione viene realizzata insieme a MAS al termine del trattamento.</p>
      </div>

      {/* Form consulenza */}
      <div ref={formRef} className="card p-5 mt-8 scroll-mt-20">
        <h2 className="serif text-xl mb-1">Richiedi una consulenza</h2>
        <p className="text-xs text-gray-500 mb-4">Ti ricontattiamo noi. Nessun account necessario.</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2">
            {TREATMENTS.map((t) => (
              <button type="button" key={t.id} data-testid={`trico-tt-${t.id}`} onClick={() => setTreatment(t.id)}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium border ${treatment === t.id ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>{t.label}</button>
            ))}
          </div>
          <input data-testid="trico-name" required placeholder="Il tuo nome" className={inp} value={f.name} onChange={set("name")} />
          <input data-testid="trico-phone" required type="tel" placeholder="Il tuo telefono" className={inp} value={f.phone} onChange={set("phone")} />
          <input data-testid="trico-email" placeholder="Email (facoltativa)" className={inp} value={f.email} onChange={set("email")} />

          <div>
            <div className="text-sm text-gray-600 mb-1">Disponibile alla video recensione a fine trattamento?</div>
            <div className="flex gap-2">
              <button type="button" data-testid="trico-video-yes" onClick={() => setVideo(true)}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium border ${video === true ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>Sì — 1.000 €</button>
              <button type="button" data-testid="trico-video-no" onClick={() => setVideo(false)}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium border ${video === false ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200 text-gray-500"}`}>No — 1.250 €</button>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-600 cursor-pointer" data-testid="trico-photo-label">
              <Camera size={18} color="#7C1FD6" />
              Aggiungi foto per una prima valutazione (facoltative)
              <input data-testid="trico-photos" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onFiles} />
            </label>
            {photos.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p.preview} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                    <button type="button" data-testid={`trico-photo-remove-${i}`} onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-0.5"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button data-testid="trico-submit" disabled={busy} className="btn-primary w-full">{busy ? "Invio…" : "Richiedi una consulenza"}</button>
        </form>
      </div>

      <div className="mt-6 text-center">
        <WhatsAppButton msg="Ciao MAS STUDIO Rivoli (TO), vorrei informazioni sulla tricopigmentazione. — richiesta inviata dall'app MAS" testid="trico-whatsapp" />
      </div>
    </Page>
  );
}
